import React, { useEffect, useMemo, useRef, useState } from "react";

import RightArrow from "@uui/ChevronRight";
import {
  filter,
  flow,
  includes,
  isEmpty,
  isEqual,
  isNil,
  toLower,
} from "lodash/fp";

import Divider from "@mui/material/Divider";
import Popover, { PopoverProps } from "@mui/material/Popover";
import Typography from "@mui/material/Typography";

import { UNKNOWN_SPEAKER } from "@app/analytics/MeetingReport/constants";
import type { TranscriptItem } from "@app/analytics/MeetingReport/types";
import {
  isAnonymousSpeaker,
  isTurnParagraph,
} from "@app/analytics/MeetingReport/utils";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import Avatar from "@rui/Avatar";
import LoadSpinner from "@rui/LoadSpinner";
import Menu from "@rui/Menu";
import MenuItem from "@rui/MenuItem";
import Stack, { Row } from "@rui/Stack";

import {
  POPOVER_SLOT_PROPS,
  EditSpeakerMenu,
  OptionButton,
  SuggestedSpeakerRow,
  PopoverTextField,
  PopoverButton,
} from "./styles";

const filterSuggestions = (value: string, suggestions: string[]) => {
  if (!suggestions) return [];
  return filter(flow(toLower, includes(toLower(value))), suggestions);
};

interface EditSpeakerPopoverProps extends Omit<PopoverProps, "onClose"> {
  onClose: () => void;
  existingName: string | null;
  suggestions: string[];
  suggestionsLoading: boolean;
  onEditSpeaker: (
    existingName: string | null,
    newName: string | null,
    action: "replace" | "update" | "untag"
  ) => void;
  editLoading?: boolean;
  transcriptItems: TranscriptItem[];
}

const EditSpeakerPopover = ({
  onEditSpeaker,
  editLoading,
  existingName,
  suggestions,
  suggestionsLoading,
  transcriptItems,
  ...props
}: EditSpeakerPopoverProps) => {
  const [value, setValue] = useState<string>("");
  const [filteredSuggestions, setFilteredSuggestions] = useState(suggestions);
  const [isEditing, setIsEditing] = useState(false);

  const [currentOption, setCurrentOption] = useState("");
  const [activeIndex, setActiveIndex] = useState<null | number>(null);

  const existingInputRef = useRef<HTMLInputElement>();

  const selectedSpeakerTurnCount = useMemo(() => {
    const name = existingName === UNKNOWN_SPEAKER ? null : existingName;

    return filter(
      (item) => isTurnParagraph(item) && item.turn.speaker === name,
      transcriptItems
    ).length;
  }, [transcriptItems, existingName]);

  const canApplyToAll = existingName != null && selectedSpeakerTurnCount > 1;

  const {
    anchorElement,
    onClick: onClickOptions,
    open: isOptionsOpen,
    onClose: onCloseOptions,
  } = useAnchorElement();

  const {
    anchorElement: editMenuAnchor,
    onClick: onClickEditMenu,
    open: editMenuOpen,
    onClose: onCloseEditMenu,
  } = useAnchorElement();

  useEffect(() => {
    if (!isEqual(suggestions, filteredSuggestions)) {
      setFilteredSuggestions(suggestions);
    }
  }, [suggestions]);

  const handleInputRef = (node: HTMLInputElement) => {
    existingInputRef.current = node;
    node?.focus();
  };

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const newSuggestions = filterSuggestions(e.target.value, suggestions ?? []);
    setValue(e.target.value);
    setFilteredSuggestions(newSuggestions);
  };

  const resetState = () => {
    setValue("");
    setFilteredSuggestions(suggestions);
    setIsEditing(false);
    setActiveIndex(null);
    setCurrentOption("");
  };

  const handleClose = () => {
    onCloseOptions();
    props.onClose();
    resetState();
  };

  const handleSelectName = (name: string) => {
    onEditSpeaker(existingName, name, "replace");
    handleClose();
  };

  const handleSelectNameForAll = (name: string) => {
    onEditSpeaker(existingName, name, "update");
    handleClose();
  };

  const handleUntag = () => {
    onEditSpeaker(existingName, null, "untag");
    handleClose();
  };

  const handleAddName = (
    e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (isEmpty(value)) return;

    if (canApplyToAll) {
      // give the user the option to apply to single instance or all instances
      setCurrentOption(value);
      onClickEditMenu(e as React.MouseEvent<HTMLElement>);
      return;
    }

    handleSelectName(value);
  };

  const handleEditName = () => {
    setIsEditing(true);
  };

  const handleSaveExisting = () => {
    if (existingInputRef.current) {
      onEditSpeaker(existingName, existingInputRef.current.value, "update");
    }
    handleClose();
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (
    event
  ) => {
    switch (event.key) {
      case "Enter":
        event.preventDefault();
        handleAddName(event);
        break;
      default:
        break;
    }
  };

  const handleEditSpeakerMenuClose = () => {
    onCloseEditMenu();
    handleClose();
  };

  const handleClickApplyToInstance = () => {
    handleSelectName(currentOption);
    handleEditSpeakerMenuClose();
  };

  const handleClickApplyToAll = () => {
    handleSelectNameForAll(currentOption);
    handleEditSpeakerMenuClose();
  };

  const handleClickSuggestion = (
    e: React.MouseEvent<HTMLElement>,
    name: string,
    index: number
  ) => {
    if (!canApplyToAll) return handleSelectName(name);

    // give the user the option to apply to single instance or all instances
    setCurrentOption(name);
    setActiveIndex(index);
    onClickEditMenu(e);
    return;
  };

  return (
    <>
      <Popover
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{ margin: "2px" }}
        slotProps={POPOVER_SLOT_PROPS}
        {...props}
        onClose={handleClose}
      >
        <Stack width="280px" padding="12px">
          {!isAnonymousSpeaker(existingName) && !isNil(existingName) && (
            <>
              <Row variant="apart" height="34px">
                {isEditing ? (
                  <>
                    <PopoverTextField
                      placeholder="Enter a name..."
                      defaultValue={existingName}
                      inputRef={handleInputRef}
                    />
                    <PopoverButton onClick={handleSaveExisting}>
                      Save
                    </PopoverButton>
                  </>
                ) : (
                  <>
                    <Row variant="left" spacing={1} flexGrow={1}>
                      <Avatar name={existingName} />
                      <Typography
                        variant="bodySecondaryReg"
                        color="shades.neutral.700"
                        flexGrow={1}
                      >
                        {existingName}
                      </Typography>
                    </Row>
                    <OptionButton onClick={onClickOptions} />
                    <Menu
                      open={isOptionsOpen}
                      onClose={onCloseOptions}
                      anchorEl={anchorElement}
                    >
                      <MenuItem onClick={handleEditName}>Edit name</MenuItem>
                      <MenuItem onClick={handleUntag}>Untag</MenuItem>
                    </Menu>
                  </>
                )}
              </Row>
              <Divider sx={{ width: "100%", my: 1 }} />
            </>
          )}
          <Typography variant="bodySecondaryMed" pl={0.5} pb={1}>
            Suggested speakers
          </Typography>
          <Stack
            height={suggestionsLoading ? "auto" : "200px"}
            overflow="auto"
            spacing="2px"
            pb={1}
          >
            {suggestionsLoading ? (
              <Stack flex={1} variant="center" py={1}>
                <LoadSpinner size={24} />
              </Stack>
            ) : isEmpty(filteredSuggestions) ? (
              <Typography
                variant="labelReg"
                color="shades.neutral.400"
                fontStyle="italic"
              >
                No suggestions
              </Typography>
            ) : (
              filteredSuggestions.map((option, i) => {
                const isActive = i === activeIndex;
                return (
                  <SuggestedSpeakerRow
                    spacing="10px"
                    key={option}
                    onClick={(e) => handleClickSuggestion(e, option, i)}
                    sx={
                      isActive ? { bgcolor: "shades.primary.100" } : undefined
                    }
                  >
                    <Row variant="left" spacing="10px">
                      <Avatar name={option} />
                      <Typography
                        variant="bodySecondaryReg"
                        color="shades.neutral.700"
                      >
                        {option}
                      </Typography>
                    </Row>
                    {canApplyToAll && (
                      <RightArrow
                        className="suggested-speaker-chevron"
                        sx={{
                          fontSize: "16px",
                          opacity: isActive ? "1 !important" : undefined,
                        }}
                      />
                    )}
                  </SuggestedSpeakerRow>
                );
              })
            )}
          </Stack>
          <Divider sx={{ width: "100%", mb: 1 }} />
          <Row variant="left" spacing={1}>
            <PopoverTextField
              placeholder="Type new name..."
              value={value}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
            />
            <PopoverButton
              loading={editLoading}
              variant="contained"
              disabled={isEmpty(value)}
              onClick={handleAddName}
            >
              Tag
            </PopoverButton>
          </Row>
        </Stack>
      </Popover>

      <EditSpeakerMenu
        open={editMenuOpen}
        onClose={handleEditSpeakerMenuClose}
        anchorEl={editMenuAnchor}
        disableAutoFocusItem
      >
        <MenuItem onClick={handleClickApplyToAll}>
          Apply to all {existingName} ({selectedSpeakerTurnCount})
        </MenuItem>
        <MenuItem onClick={handleClickApplyToInstance}>
          Only apply to this instance
        </MenuItem>
      </EditSpeakerMenu>
    </>
  );
};

export default EditSpeakerPopover;
